/**
 * DeepExploreSection - Section container for deep-dive content blocks.
 *
 * Previously a collapsible accordion; now always-expanded to avoid
 * maxHeight / overflow:hidden truncating tall children (e.g. R10).
 * Renders a simple titled section with a subtle top divider.
 *
 * 深度探索区域容器：始终展开，不可折叠。带标题和分割线。
 */
import type { CSSProperties, ReactNode } from 'react';

// ----- Types -----

export interface DeepExploreSectionProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** @deprecated Ignored — section is always expanded. */
  defaultExpanded?: boolean;
}

// ----- Styles -----

const sectionStyle: CSSProperties = {
  borderTop: '1px solid var(--border-subtle)',
  marginTop: 'var(--space-xl)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-xs)',
  padding: 'var(--space-2xl) var(--space-xl) var(--space-md)',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--fs-xl)',
  color: 'var(--text-primary)',
  fontWeight: 'var(--fw-semibold)',
  lineHeight: 1.2,
};

const subtitleStyle: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-tertiary)',
};

// ----- Component -----

export default function DeepExploreSection({
  title,
  subtitle,
  children,
}: DeepExploreSectionProps) {
  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>{title}</div>
        {subtitle != null && <div style={subtitleStyle}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}
